import { Cross2Icon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import { IBadge } from '@clickersphere/core/entities/IBadge';
import {
  BADGE_COLOR_HEX_MAP,
  BADGE_COLOR_PASTEL_MAP,
  BADGE_COLOR_SOFT_TEXT_MAP,
} from '@clickersphere/core/enums/BadgeColorEnum';
import cn from '../../../app/utils/cn';

interface IBadgeChipProps {
  badge: IBadge;
  onRemove?: (badge: IBadge) => void;
  archived?: boolean;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
  removable?: boolean;
}

export default function BadgeChip({
  badge,
  onRemove,
  archived = false,
  size = 'sm',
  className,
  removable = true,
}: IBadgeChipProps) {
  const { t } = useTranslation();
  const dotColor = BADGE_COLOR_HEX_MAP[badge.color];
  const backgroundColor = BADGE_COLOR_PASTEL_MAP[badge.color];
  const textColor = BADGE_COLOR_SOFT_TEXT_MAP[badge.color];

  const sizeClasses = {
    xs: 'text-[10px] px-1.5 py-0.5 gap-1',
    sm: 'text-xs px-2 py-0.5 gap-1.5',
    md: 'text-sm px-2.5 py-1 gap-1.5',
  };

  const dotSize = {
    xs: 'h-1.5 w-1.5',
    sm: 'h-2 w-2',
    md: 'h-2 w-2',
  };

  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center rounded-md font-medium ring-1 ring-black/5 ring-inset dark:ring-white/10',
        sizeClasses[size],
        archived && 'opacity-60',
        className,
      )}
      style={{
        backgroundColor,
        color: textColor,
      }}
      title={
        archived
          ? t('badge.chip.archivedTitle', { name: badge.name })
          : badge.name
      }
    >
      <span
        aria-hidden
        className={cn('shrink-0 rounded-full', dotSize[size])}
        style={{ backgroundColor: dotColor }}
      />
      <span className="truncate">{badge.name}</span>
      {removable && onRemove && (
        <button
          type="button"
          aria-label={t('badge.chip.remove', { name: badge.name })}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(badge);
          }}
          className="flex shrink-0 cursor-pointer items-center justify-center rounded-full opacity-70 hover:opacity-100"
          style={{ color: textColor }}
        >
          <Cross2Icon className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}
